import React, { createContext, useContext, useState } from 'react';
import { gql } from '@apollo/client';
import { client } from '../apollo/client';
import { HttpLink } from '@apollo/client';

const AuthContext = createContext(null);

// Definir la mutación de login
const LOGIN_MUTATION = gql`
  mutation Login($input: inputLogin) {
    login(input: $input) {
      token
      refreshToken
      logo
      displayName
      app_logo
      isTemporalPassword
    }
  }
`;

// Query para obtener datos del token
const TOKEN_QUERY = gql`
  query Token {
    token {
      loggedUser {
        role
        status
      }
      userData {
        activo
        email
        id
        role
        status
        perfil {
          permisos {
            dashboard
            multasMuni
            usr_perfilvelocidad
            usr_report1
            usr_report1cliente
            usr_report1clientexpress
            usr_report1express
            usr_reportflota
            usr_rpt2velocidad
            verMapa
          }
          setting {
            IVA
            cantModifyLogo
            cargoServ
            difAlertaVelocidad
            disWeekend
            grafTransmisionxDias
            minDifVelocidad
            neCargoServ
            serviceMatching
            verConsumosTAG
            verEdoFlota
            verWeekend
          }
        }
      }
    }
  }
`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  const login = async (username, password) => {
    try {
      // Primero hacemos el login
      const { data: loginData } = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: {
          input: {
            username,
            password
          }
        },
        context: {
          headers: {
            'usuario': username,
            'language': 'es'
          }
        }
      });

      const { token: newToken, displayName, ...userData } = loginData.login;
      
      // Guardar el token y displayname
      localStorage.setItem('token', newToken);
      localStorage.setItem('displayname', displayName);
      setToken(newToken);

      // Obtener los datos adicionales del usuario
      const { data: tokenData } = await client.query({
        query: TOKEN_QUERY,
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'usuario': username,
            'language': 'es'
          }
        }
      });

      // Combinar los datos del login con los datos del token
      const completeUserData = {
        ...userData,
        displayName,
        ...tokenData.token
      };
      
      if(completeUserData?.loggedUser?.status === 'INACTIVO' || completeUserData?.loggedUser?.role !== 1){
        setIsAuthenticated(false);
        throw new Error('Usuario inactivo o no tiene permisos para acceder a la aplicación');
      }

      setUser(completeUserData);
      setIsAuthenticated(true);

      return completeUserData;
    } catch (error) {
      setIsAuthenticated(false);
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const serverError = error.graphQLErrors[0];
        throw new Error(serverError.messageBack || serverError.message || 'Error desconocido');
      }
      throw new Error('Error al iniciar sesión');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('displayname');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    client.resetStore();
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}; 